import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sparkle, SpinnerGap, CaretDown, CaretUp } from "@phosphor-icons/react";

export function SectionEditor({ section, report, onUpdate, onGenerateNote, generatingNote }) {
  const st = section.section_type;
  const [expanded, setExpanded] = useState(false);

  if (st === "cover") {
    return (
      <div className="space-y-4" data-testid="editor-cover">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wider">Cover Page</h3>
        <div>
          <label className="text-xs text-[var(--text-muted)] mb-1 block">Report Title</label>
          <Input value={section.title || ""} onChange={(e) => onUpdate({ title: e.target.value })} className="bg-[var(--bg-base)] border-[var(--border-strong)]" data-testid="cover-title-input" />
        </div>
        <div>
          <label className="text-xs text-[var(--text-muted)] mb-1 block">Client Name</label>
          <Input value={report?.client_name || ""} readOnly className="bg-[var(--bg-base)] border-[var(--border-strong)] opacity-60" />
        </div>
        <div>
          <label className="text-xs text-[var(--text-muted)] mb-1 block">Period</label>
          <Input value={`${report?.report_period?.start_date || ""} to ${report?.report_period?.end_date || ""}`} readOnly className="bg-[var(--bg-base)] border-[var(--border-strong)] opacity-60" />
        </div>
        <div>
          <label className="text-xs text-[var(--text-muted)] mb-1 block">Tagline (optional)</label>
          <Input value={section.body || ""} onChange={(e) => onUpdate({ body: e.target.value })} placeholder="e.g. Strategic content that drives results" className="bg-[var(--bg-base)] border-[var(--border-strong)]" data-testid="cover-tagline-input" />
        </div>
      </div>
    );
  }

  if (st === "strategy_note") {
    return (
      <div className="space-y-4" data-testid="editor-strategy-note">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wider">Strategy Note</h3>
          <Button onClick={onGenerateNote} disabled={generatingNote} variant="outline" className="h-10 sm:h-8 text-xs border-[#FF4400]/30 text-[#FF4400] hover:bg-[#FF4400]/10" data-testid="generate-note-btn">
            {generatingNote ? <SpinnerGap size={14} className="mr-1 animate-spin" /> : <Sparkle size={14} className="mr-1" />}
            {generatingNote ? "Generating..." : "Generate with AI"}
          </Button>
        </div>
        <div>
          <label className="text-xs text-[var(--text-muted)] mb-1 block">Title</label>
          <Input value={section.title || ""} onChange={(e) => onUpdate({ title: e.target.value })} className="bg-[var(--bg-base)] border-[var(--border-strong)]" data-testid="note-title-input" />
        </div>
        <div>
          <label className="text-xs text-[var(--text-muted)] mb-1 block">Body</label>
          <Textarea value={section.body || ""} onChange={(e) => onUpdate({ body: e.target.value })} rows={8} placeholder="Write or generate a strategic rationale..." className="bg-[var(--bg-base)] border-[var(--border-strong)] resize-y" data-testid="note-body-input" />
        </div>
      </div>
    );
  }

  if (st === "content_item") {
    const snap = section.content_snapshot || {};
    return (
      <div className="space-y-4" data-testid="editor-content-item">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wider">Content Item</h3>
        <div className="bg-[var(--bg-base)] border border-[var(--border)] rounded-lg p-4 space-y-2">
          <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
            <span className="px-2 py-0.5 rounded bg-[#FF4400]/10 text-[#FF4400] border border-[#FF4400]/20">{snap.content_type}</span>
            <span className="px-2 py-0.5 rounded bg-[var(--bg-hover)] border border-[var(--border)]">{snap.platform}</span>
          </div>
          <p className="text-sm font-medium text-[var(--text-primary)]">{snap.topic || section.title}</p>
          <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors" data-testid="expand-content-btn">
            {expanded ? <CaretUp size={12} /> : <CaretDown size={12} />}
            {expanded ? "Collapse content" : "Expand content"}
          </button>
          {expanded && (
            <div className="mt-2 text-sm text-[var(--text-secondary)] whitespace-pre-wrap leading-relaxed max-h-[300px] overflow-y-auto border-t border-[var(--border)] pt-3">
              {snap.generated_draft || "No content available"}
            </div>
          )}
        </div>
        <div>
          <label className="text-xs text-[var(--text-muted)] mb-1 block">Editor's Note (optional — shown in PDF)</label>
          <Input value={section.editors_note || ""} onChange={(e) => onUpdate({ editors_note: e.target.value })} placeholder="Brief note for the client about this piece" className="bg-[var(--bg-base)] border-[var(--border-strong)]" data-testid="editors-note-input" />
        </div>
      </div>
    );
  }

  if (st === "custom_text") {
    return (
      <div className="space-y-4" data-testid="editor-custom-text">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wider">Custom Text</h3>
        <div>
          <label className="text-xs text-[var(--text-muted)] mb-1 block">Title</label>
          <Input value={section.title || ""} onChange={(e) => onUpdate({ title: e.target.value })} className="bg-[var(--bg-base)] border-[var(--border-strong)]" data-testid="custom-title-input" />
        </div>
        <div>
          <label className="text-xs text-[var(--text-muted)] mb-1 block">Body</label>
          <Textarea value={section.body || ""} onChange={(e) => onUpdate({ body: e.target.value })} rows={8} placeholder="Write your custom content..." className="bg-[var(--bg-base)] border-[var(--border-strong)] resize-y" data-testid="custom-body-input" />
        </div>
      </div>
    );
  }

  if (st === "divider") {
    return (
      <div className="space-y-4" data-testid="editor-divider">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wider">Divider</h3>
        <div className="flex items-center justify-center py-8">
          <div className="w-2/3 border-t border-[var(--border)]" />
        </div>
        <p className="text-xs text-[var(--text-muted)] text-center">This inserts a visual separator in the PDF.</p>
      </div>
    );
  }

  return <p className="text-sm text-[var(--text-muted)]">Unknown section type</p>;
}
